// currency.js — detect visitor country and convert deal prices for display only.
// Geo: reads ff_country cookie (set by Cloudflare/Vercel middleware when available),
// falls back to navigator.language region tag.
// Exchange rates: frankfurter.app (free, no key), cached 24 h in localStorage.
// Subscription prices remain in EUR — only deal prices use this.

import { useState, useEffect } from 'react'

// Non-EUR countries we explicitly handle
const COUNTRY_CURRENCY = {
  GB: 'GBP', US: 'USD', CA: 'CAD', AU: 'AUD', NZ: 'NZD',
  CH: 'CHF', NO: 'NOK', SE: 'SEK', DK: 'DKK',
  JP: 'JPY', SG: 'SGD',
  TR: 'TRY', PL: 'PLN', RO: 'RON', HU: 'HUF', CZ: 'CZK',
}

// Exact locale → country (for common cases navigator.language doesn't split cleanly)
const LOCALE_COUNTRY = {
  'en-GB': 'GB', 'en-US': 'US', 'en-CA': 'CA', 'en-AU': 'AU', 'en-NZ': 'NZ',
  'de-CH': 'CH', 'fr-CH': 'CH', 'it-CH': 'CH',
  'nb-NO': 'NO', 'nn-NO': 'NO',
  'sv-SE': 'SE', 'da-DK': 'DK',
  'ja-JP': 'JP',
  'tr-TR': 'TR', 'pl-PL': 'PL', 'ro-RO': 'RO', 'hu-HU': 'HU', 'cs-CZ': 'CZ',
}

// Currency → display symbol
const SYMBOLS = {
  EUR: 'EUR ',  GBP: '£',    USD: '$',    CAD: 'CA$', AUD: 'A$',
  NZD: 'NZ$', CHF: 'CHF ', NOK: 'NOK ', SEK: 'SEK ',
  DKK: 'DKK ', JPY: '¥', SGD: 'S$',
  TRY: '₺', PLN: 'zł', RON: 'lei ', HUF: 'Ft ', CZK: 'Kč ',
}

const CACHE_KEY = 'ff_fx_v1'
const CACHE_TTL = 86_400_000 // 24 h
const PREF_KEY = 'ff_preferred_currency'
const CURRENCY_EVENT = 'ff_currency_change'

// Static fallback rates (from EUR) — used when frankfurter.app is unreachable.
// Approximate values; refresh periodically. Display-only, not financial precision.
const FALLBACK_RATES = {
  GBP: 0.85, USD: 1.08, CAD: 1.47, AUD: 1.65, NZD: 1.80,
  CHF: 0.96, NOK: 11.70, SEK: 11.20, DKK: 7.46,
  JPY: 162.0, SGD: 1.45,
  TRY: 36.0, PLN: 4.30, RON: 4.98, HUF: 395.0, CZK: 25.3,
}

// Currencies available in the selector UI
export const SELECTOR_CURRENCIES = ['EUR', 'GBP', 'USD', 'CHF', 'SEK', 'NOK', 'DKK', 'TRY', 'PLN', 'RON', 'HUF', 'CZK']

function detectCountry() {
  if (typeof window === 'undefined') return null
  // 1. Cookie ff_country=XX (set by middleware when Cloudflare/Vercel geo header is present)
  const m = document.cookie.match(/(?:^|;\s*)ff_country=([A-Z]{2})/)
  if (m) return m[1]
  // 2. navigator.language exact match
  const lang = navigator.language ?? ''
  if (LOCALE_COUNTRY[lang]) return LOCALE_COUNTRY[lang]
  // 3. Region tag from locale (e.g. "en-AU" → "AU")
  return lang.split('-')[1]?.toUpperCase() ?? null
}

function loadCachedRates() {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { ts, rates } = JSON.parse(raw)
    if (Date.now() - ts < CACHE_TTL) return rates
  } catch {}
  return null
}

// Persist user currency choice and notify all useCurrency() instances on the page
export function setCurrencyPreference(code) {
  if (typeof window === 'undefined') return
  localStorage.setItem(PREF_KEY, code)
  window.dispatchEvent(new CustomEvent(CURRENCY_EVENT, { detail: { currency: code } }))
}

// useCurrency() — returns { currency, formatDealPrice }
// formatDealPrice(eurPrice) → { primary: '£78', secondary: '€91' } or { primary: '€91', secondary: null }
export function useCurrency() {
  const [currency, setCurrencyState] = useState('EUR')
  const [rates, setRates]            = useState(null)

  function fetchRates() {
    const cached = loadCachedRates()
    if (cached) { setRates(cached); return }
    fetch('/api/exchange-rates')
      .then(r => r.json())
      .then(json => {
        const r = json.rates ?? {}
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), rates: r })) } catch {}
        setRates(r)
      })
      .catch(() => { setRates(FALLBACK_RATES) }) // network failure → use static fallback
  }

  useEffect(() => {
    // 1. User preference from localStorage takes precedence over auto-detect
    const pref = localStorage.getItem(PREF_KEY)
    if (pref && SELECTOR_CURRENCIES.includes(pref)) {
      setCurrencyState(pref)
      if (pref !== 'EUR') fetchRates()
      return
    }
    // 2. Auto-detect from country/locale
    const country = detectCountry()
    const cur = (country && COUNTRY_CURRENCY[country]) ?? 'EUR'
    setCurrencyState(cur)
    if (cur !== 'EUR') fetchRates()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync with currency changes dispatched by setCurrencyPreference() (e.g. from Navbar)
  useEffect(() => {
    function handler(e) {
      const cur = e.detail?.currency
      if (!cur) return
      setCurrencyState(cur)
      if (cur !== 'EUR') fetchRates()
      else setRates(null)
    }
    window.addEventListener(CURRENCY_EVENT, handler)
    return () => window.removeEventListener(CURRENCY_EVENT, handler)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function formatDealPrice(eurPrice) {
    if (currency === 'EUR') {
      return { primary: `€${eurPrice}`, secondary: null }
    }
    // Use live rates when available, fall back to static table so conversion
    // works immediately (before API resolves) and when the API is unreachable.
    const effectiveRates = rates ?? FALLBACK_RATES
    const rate = effectiveRates[currency]
    if (!rate) return { primary: `€${eurPrice}`, secondary: null }
    const sym = SYMBOLS[currency] ?? `${currency} `
    return { primary: `${sym}${Math.round(eurPrice * rate)}`, secondary: `€${eurPrice}` }
  }

  // For subscription prices: preserve 2 decimals for small amounts, round for large
  function formatPlanPrice(eurAmount) {
    if (currency === 'EUR') return null  // caller shows EUR natively
    const effectiveRates = rates ?? FALLBACK_RATES
    const rate = effectiveRates[currency]
    if (!rate) return null
    const sym = SYMBOLS[currency] ?? `${currency} `
    const converted = eurAmount * rate
    const display = converted < 15
      ? converted.toFixed(2).replace('.', ',')
      : Math.round(converted)
    return `${sym}${display}`
  }

  return { currency, formatDealPrice, formatPlanPrice }
}

// Simple symbol lookup for deals that already carry their own currency code
export function currencySymbol(code) {
  if (!code) return '\u20ac';
  return SYMBOLS[code.toUpperCase()] ?? code + ' ';
}
